<template>
  <div class="sale-detail">
    <p class="sale-detail__price">
      <template v-if="priceNumber !== empty">{{ priceNumber }}<span class="app-eur">€</span></template>
      <template v-else>{{ empty }}</template>
    </p>
    <div class="sale-detail__route">
      <div class="sale-detail__stops">
        <div class="sale-detail__stop">
          <span class="sale-detail__dot" aria-hidden="true"></span>
          <span class="sale-detail__name"><span class="app-sr-only">Von </span>{{ fromName }}</span>
        </div>
        <span class="sale-detail__line" aria-hidden="true"></span>
        <div class="sale-detail__stop">
          <span class="sale-detail__dot sale-detail__dot--end" aria-hidden="true"></span>
          <span class="sale-detail__name"><span class="app-sr-only">Nach </span>{{ toName }}</span>
        </div>
      </div>
      <p v-if="tripText" class="sale-detail__trip">{{ tripText }}</p>
    </div>
    <section v-for="section in sections" :key="section.key" class="sale-detail__section">
      <h3 class="sale-detail__heading">{{ section.title }}</h3>
      <dl class="sale-detail__list">
        <template v-for="row in section.rows">
          <dt :key="'t-' + row.label" class="sale-detail__label">{{ row.label }}</dt>
          <dd :key="'d-' + row.label" class="sale-detail__value">
            <app-icon v-if="row.icon" :name="row.icon" :size="16" class="sale-detail__icon" />{{ row.value }}
          </dd>
        </template>
      </dl>
    </section>
  </div>
</template>

<script>
// Inhalt des Detailbereichs „Verkauf“ (Spezifikation 4.2.7, gen_r3.py:1216–1245): Preis 18/24 600, Strecke ohne
// Rahmen (Start hohl, Ziel gefüllt, gepunktete Führungslinie), darunter „Linie · Fahrt · Hin-/Rückfahrt“, dann die
// Daten in Abschnitten (Kundenwunsch 12.10.2026, wie die Spalten der früheren Verkaufsliste): Verkauf (Zahlart mit
// Symbol aus zahlart.js, beim Gutschein Code und Art des Scans) · Fahrgast
// (EBE-Angaben der Drucker-App in custom_data: salutation, firstName, lastName, birthday, address, postalCode, city,
// guardian*, remarks; ältere Zeilen in passenger_name als „Vorname#Nachname#PLZ#Ort#Straße“; Passnummer) · Rollen
// (Rollen-Tracking MVV, custom_data.rollNumbers mit je 10 Ziffern: 6 Rolle, 4 Abschnitt; Papierlänge aus
// custom_data.paperFeedMm) · Gerät (Drucker, Druckertyp,
// Drucker-Seriennummer, App-Version aus note „android-v…“ samt Hinweis „Tarifversion … manuell“, Fahrplan- und
// Tarifversion aus version_id/tariff_version_id, sobald die Schnittstelle sie liefert, lokale ID, Druckprotokoll bei
// Fehlern). Abschnitte und Zeilen ohne Wert entfallen. Nur Anzeige; Stornieren (Fuß) und Schließen stehen in der Seite.
import moment from 'moment';
import { formatNumber, formatDateTime, LEER } from '@/utils/format';
import { zahlartInfo, istKarteExtern } from './zahlart';

function text(value) {
  return value === null || value === undefined || value === '' ? LEER : String(value);
}

function present(value) {
  return value !== null && value !== undefined && value !== '';
}

// custom_data der Schnittstelle (JSON-Text) sicher lesen; ohne oder bei Fehler {}
function parseCustom(raw) {
  if (!raw) {
    return {};
  }
  if (typeof raw === 'object') {
    return raw;
  }
  try {
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : {};
  } catch (e) {
    return {};
  }
}

function join(parts, sep) {
  return parts.filter(present).join(sep);
}

// Gutschein-Scan der Drucker-App: Format (ML Kit) und Quelle lesbar
function gutscheinFormat(format) {
  const namen = { QR_CODE: 'QR-Code', AZTEC: 'Aztec-Code', DATA_MATRIX: 'Data Matrix', PDF417: 'PDF417' };
  return present(format) ? namen[format] || String(format).replace(/_/g, ' ') : null;
}

function gutscheinQuelle(quelle) {
  const namen = { scanner: 'Scan-Taste', kamera: 'Kamera', tastatur: 'Scanner (Tastatur)' };
  return present(quelle) ? namen[quelle] || String(quelle) : null;
}

// nur Zeilen mit Wert
function filled(rows) {
  return rows.filter(row => present(row.value) && row.value !== LEER);
}

export default {
  name: 'SaleDetail',
  props: {
    sale: { type: Object, required: true },
    // Anzeigename des Fahrers (aus der Fahrerliste der Seite, sonst Benutzername)
    driverName: { type: String, default: null }
  },
  data() {
    return { empty: LEER };
  },
  computed: {
    priceNumber() {
      return formatNumber(this.sale.price, 2);
    },
    fromName() {
      return text(this.sale.location_from_name);
    },
    toName() {
      return text(this.sale.location_to_name);
    },
    zahlart() {
      return zahlartInfo(this.sale);
    },
    // Text der Zahlart; Karte am externen Terminal mit Zusatz
    zahlartText() {
      if (this.zahlart.value === 'karte' && istKarteExtern(this.sale)) {
        return 'Karte (externes Terminal)';
      }
      return this.zahlart.text;
    },
    isStorno() {
      return this.sale.is_cancellation == 1;
    },
    tripText() {
      const s = this.sale;
      const parts = [];
      if (present(s.line_id)) {
        parts.push('Linie ' + s.line_id);
      }
      if (present(s.trip_id)) {
        parts.push('Fahrt ' + s.trip_id);
      }
      if (present(s.is_outward_direction)) {
        parts.push(s.is_outward_direction == 1 ? 'Hinfahrt' : 'Rückfahrt');
      }
      return parts.join(' · ');
    },
    validTo() {
      const v = this.sale.valid_to;
      if (!present(v)) {
        return LEER;
      }
      // nur lesbare Zeitangaben umformatieren, sonst Rohwert
      const m = moment(v, moment.ISO_8601, true);
      return m.isValid() ? formatDateTime(m) : String(v);
    },
    custom() {
      return parseCustom(this.sale.custom_data);
    },
    combText() {
      const s = this.sale;
      if (!present(s.comb_from) && !present(s.comb_to)) {
        return null;
      }
      const route = join([s.comb_from, s.comb_to], ' → ');
      return present(s.comb_alternative) ? route + ' (über ' + s.comb_alternative + ')' : route;
    },
    saleRows() {
      const s = this.sale;
      const rows = [
        { label: 'Ticket', value: text(s.ticket_name) },
        { label: 'Tarif', value: text(s.tariff_name) },
        { label: 'Preisstufe', value: s.price_level },
        { label: 'Waben', value: this.combText },
        { label: 'Personen', value: text(s.ticket_count) },
        { label: 'Zahlart', value: this.zahlartText, icon: this.zahlart.icon },
        { label: 'Gültig bis', value: this.validTo },
        { label: 'Verkauft', value: formatDateTime(s.sale_time) },
        { label: 'Fahrer', value: text(this.driverName || s.username) }
      ];
      if (this.zahlart.value === 'karte' && !istKarteExtern(s)) {
        rows.push({ label: 'SumUp-Transaktion', value: text(s.sumup_transaction_code) });
      }
      // Gutschein: gescannter Code (Text) und Art des Codes, z. B. „QR_CODE · Scan-Taste“
      if (this.zahlart.value === 'gutschein') {
        const c = this.custom;
        rows.push({ label: 'Gutschein-Code', value: present(c.voucherCode) ? String(c.voucherCode) : 'nicht gescannt' });
        rows.push({ label: 'Code-Art', value: join([gutscheinFormat(c.voucherCodeFormat), gutscheinQuelle(c.voucherCodeSource)], ' · ') });
      }
      if (this.isStorno) {
        rows.push({ label: 'Storniert von', value: text(s.canceled_by_username) });
      }
      return filled(rows);
    },
    // Fahrgast: EBE-Angaben aus custom_data (Name, Geburtsdatum, Anschrift, Erziehungsberechtigte, Bemerkung), sonst
    // passenger_name; dazu die Passnummer
    passengerRows() {
      const s = this.sale;
      const c = this.custom;
      // ältere Zeilen: „Vorname#Nachname#PLZ#Ort#Straße“ in passenger_name
      const old = typeof s.passenger_name === 'string' && s.passenger_name.indexOf('#') !== -1
        ? s.passenger_name.split('#') : null;
      const name = join([c.salutation, c.firstName, c.lastName], ' ')
        || (old ? join([old[0], old[1]], ' ') : s.passenger_name);
      const street = c.address || (old ? old[4] : null);
      const city = join([c.postalCode || (old ? old[2] : null), c.city || (old ? old[3] : null)], ' ');
      // Vertreter nur, wenn Name oder Geburtsdatum gesetzt sind (Anrede und Art sind in der App vorbelegt)
      const hasGuardian = present(c.guardianFirstName) || present(c.guardianLastName) || present(c.guardianBirthday);
      const guardian = hasGuardian
        ? join([
          join([c.guardianSalutation, c.guardianFirstName, c.guardianLastName], ' '),
          c.guardianRepresentativeType ? '(' + c.guardianRepresentativeType + ')' : null,
          present(c.guardianBirthday) && c.guardianBirthday !== c.birthday ? c.guardianBirthday : null
        ], ', ').replace(', (', ' (')
        : null;
      return filled([
        { label: 'Name', value: name },
        { label: 'Geburtsdatum', value: c.birthday },
        { label: 'Anschrift', value: join([street, city], ', ') },
        { label: 'Vertreter', value: guardian },
        { label: 'Bemerkung', value: c.remarks },
        { label: 'Passnummer', value: s.passnr }
      ]);
    },
    // Rollen-Tracking: Rollennummern „RRRRRRAAAA“ – erste 6 Zeichen die Rolle, der Rest der Abschnitt; dazu die vom
    // GeBE-Drucker gemessene Papierlänge des Drucks (custom_data.paperFeedMm, App ab 2.4.45). Die Länge steht auch ohne
    // Rollennummer da: dann hat der Drucker gedruckt, aber der Scanner nichts gelesen.
    rollRows() {
      const numbers = Array.isArray(this.custom.rollNumbers) ? this.custom.rollNumbers.map(String).filter(present) : [];
      const rolls = numbers.map(n => n.substring(0, 6)).filter((v, i, all) => all.indexOf(v) === i);
      const feed = Number(this.custom.paperFeedMm);
      return filled([
        { label: rolls.length === 1 ? 'Rolle' : 'Rollen', value: rolls.join(', ') },
        { label: numbers.length === 1 ? 'Abschnitt' : 'Abschnitte', value: numbers.map(n => n.substring(6)).filter(present).join(', ') },
        { label: 'Papierlänge', value: feed > 0 ? formatNumber(feed, 1) + ' mm' : null }
      ]);
    },
    // note der Drucker-App: „android-v2.4.50“, ggf. „ | Tarifversion 12 manuell (gültig ab 01.10.2026)“; Web-Storno
    // „webapp-v…“
    noteParts() {
      const note = present(this.sale.note) ? String(this.sale.note) : '';
      const [head, ...rest] = note.split(' | ');
      const m = /^(android|webapp)-v(.+)$/.exec(head.trim());
      return {
        version: m ? m[2] + (m[1] === 'webapp' ? ' (Web)' : '') : head.trim(),
        hint: rest.join(' | ').trim()
      };
    },
    // Gerät: Drucker, Druckertyp und Seriennummer (Druckdiagnose in custom_data), App-Version, Fahrplan-/Tarifversion
    // (version_id/tariff_version_id, sobald die Schnittstelle sie liefert), lokale Verkaufs-ID, Druckprotokoll bei Fehler
    deviceRows() {
      const s = this.sale;
      const c = this.custom;
      return filled([
        { label: 'Gerät', value: s.printer_id },
        { label: 'Druckertyp', value: c.printerType },
        { label: 'Drucker-Seriennr.', value: c.printerSerial },
        { label: 'App-Version', value: this.noteParts.version },
        { label: 'Hinweis', value: this.noteParts.hint },
        { label: 'Fahrplanversion', value: s.version_id },
        { label: 'Tarifversion', value: s.tariff_version_id },
        { label: 'Lokale ID', value: s.local_id },
        { label: 'Druckprotokoll', value: c.gebeLog }
      ]);
    },
    sections() {
      return [
        { key: 'verkauf', title: 'Verkauf', rows: this.saleRows },
        { key: 'fahrgast', title: 'Fahrgast', rows: this.passengerRows },
        { key: 'rollen', title: 'Rollen', rows: this.rollRows },
        { key: 'geraet', title: 'Gerät', rows: this.deviceRows }
      ].filter(section => section.rows.length);
    }
  }
};
</script>

<style scoped>
.sale-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
p.sale-detail__price {
  margin: 0;
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -.01em;
  color: var(--ink);
  white-space: nowrap;
}
.sale-detail__route {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sale-detail__stops {
  display: flex;
  flex-direction: column;
}
.sale-detail__stop {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.sale-detail__dot {
  width: 10px;
  height: 10px;
  flex: none;
  box-sizing: border-box;
  border: 2px solid var(--d-foc);
  border-radius: 50%;
  background: transparent;
}
.sale-detail__dot--end {
  background: var(--d-foc);
}
.sale-detail__line {
  width: 0;
  height: 16px;
  margin-left: 4px;
  border-left: 2px dotted var(--d-prv);
}
.sale-detail__name {
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  overflow-wrap: anywhere;
}
p.sale-detail__trip {
  margin: 0;
  padding-left: 22px;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
/* Abschnitt: Linie oben, Überschrift 12/16 600 --ink-3, darunter die Datenliste */
.sale-detail__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--line-2);
}
h3.sale-detail__heading {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: .02em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.sale-detail__list {
  display: grid;
  grid-template-columns: 124px minmax(0, 1fr);
  gap: 8px 12px;
  margin: 0;
}
.sale-detail__label {
  font-size: 12px;
  line-height: 20px;
  color: var(--ink-3);
}
.sale-detail__value {
  margin: 0;
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
/* Symbol vor der Zahlart (Bar, Karte, Gutschein) */
.sale-detail__icon {
  display: inline-block;
  margin-right: 6px;
  vertical-align: -3px;
  color: var(--ink-3);
}
</style>
